import React, { useState, useEffect } from "react";
import {
  Package, Plus, Trash2, AlertTriangle, CheckCircle2,
  XCircle, Loader2, ShoppingCart, Sparkles, X, Search, Timer
} from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { useListContext } from "../contexts/ListContext";
import { useAuth } from "../contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  stocked: { color: "bg-green-500", badge: "bg-green-100 text-green-700", label: "Stocked", icon: CheckCircle2 },
  running_low: { color: "bg-amber-500", badge: "bg-amber-100 text-amber-700", label: "Running Low", icon: AlertTriangle },
  likely_out: { color: "bg-red-500", badge: "bg-red-100 text-red-700", label: "Likely Out", icon: XCircle },
};

const PantrySection = ({ onPantryItemsChange }) => {
  const [pantryItems, setPantryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSetup, setShowSetup] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedSuggestions, setSelectedSuggestions] = useState(new Set());
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [savingSuggestions, setSavingSuggestions] = useState(false);
  const [newItemName, setNewItemName] = useState("");
  const [addingItem, setAddingItem] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const { lists, currentListId, setLists } = useListContext();
  const { user } = useAuth();
  const userId = user?.id;

  const addLowItemsToList = (items) => {
    const targetList = lists.find(l => l.id === currentListId);
    if (!targetList) return;
    const existing = new Set((targetList.items || []).map(i => i.name.toLowerCase()));
    const newItems = items
      .filter(i => !existing.has(i.name.toLowerCase()))
      .map((item, idx) => ({
        id: Date.now() + Math.random() + idx,
        name: item.name,
        quantity: 1,
        completed: false,
        category: item.category || "Pantry",
        estimated_price: 3.99,
      }));
    if (newItems.length > 0) {
      setLists(prev => prev.map(list =>
        list.id === currentListId ? { ...list, items: [...newItems, ...list.items] } : list
      ));
    }
    return newItems.length;
  };

  const fetchPantry = async () => {
    try {
      const params = userId ? `?user_id=${userId}` : "";
      const res = await fetch(`${BACKEND_URL}/api/pantry/items${params}`);
      if (res.ok) {
        const data = await res.json();
        setPantryItems(data.items || []);
      }
    } catch (e) {
      console.error("Failed to fetch pantry:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPantry(); }, []); // eslint-disable-line

  // Propagate pantry changes to parent
  useEffect(() => {
    if (onPantryItemsChange) onPantryItemsChange(pantryItems);
  }, [pantryItems]); // eslint-disable-line

  const fetchSuggestions = async () => {
    setLoadingSuggestions(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/pantry/suggestions`);
      if (res.ok) {
        const data = await res.json();
        setSuggestions(data.suggestions || []);
        setSelectedSuggestions(new Set(data.suggestions?.map(s => s.name) || []));
      }
    } catch (e) {
      console.error("Failed to fetch suggestions:", e);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const openSetup = () => {
    setShowSetup(true);
    if (suggestions.length === 0) fetchSuggestions();
  };

  const toggleSuggestion = (name) => {
    setSelectedSuggestions(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const saveBulk = async () => {
    const items = suggestions
      .filter(s => selectedSuggestions.has(s.name))
      .map(s => ({ name: s.name, category: s.category, user_id: userId }));
    if (items.length === 0) return;
    setSavingSuggestions(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/pantry/items/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items })
      });
      if (res.ok) {
        setShowSetup(false);
        await fetchPantry();
      }
    } catch (e) {
      console.error("Failed to save pantry items:", e);
    } finally {
      setSavingSuggestions(false);
    }
  };

  const addSingleItem = async () => {
    if (!newItemName.trim()) return;
    setAddingItem(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/pantry/items`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newItemName.trim(), user_id: userId })
      });
      if (res.ok) {
        setNewItemName("");
        await fetchPantry();
      }
    } catch (e) {
      console.error("Failed to add item:", e);
    } finally {
      setAddingItem(false);
    }
  };

  const deleteItem = async (itemId) => {
    try {
      await fetch(`${BACKEND_URL}/api/pantry/items/${itemId}`, { method: "DELETE" });
      setPantryItems(prev => {
        const updated = prev.filter(i => i.item_id !== itemId);
        if (onPantryItemsChange) onPantryItemsChange(updated);
        return updated;
      });
    } catch (e) {
      console.error("Failed to delete item:", e);
    }
  };

  const runningLow = pantryItems.filter(i => i.status === "running_low" || i.status === "likely_out");
  const stocked = pantryItems.filter(i => i.status === "stocked");

  const groupByCategory = (items) => {
    const groups = {};
    for (const item of items) {
      const cat = item.category || "Other";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(item);
    }
    return groups;
  };

  if (loading) {
    return (
      <Card className="border-slate-200 bg-slate-50/50">
        <CardContent className="p-4 flex items-center justify-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
          <span className="text-sm text-slate-500">Loading pantry...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="border-slate-200 bg-slate-50/50" data-testid="pantry-section">
        <CardContent className="p-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => setExpanded(!expanded)}>
              <Package className="h-4 w-4 text-slate-600" />
              <h3 className="text-sm font-semibold text-slate-800">
                My Pantry
                <span className="text-xs text-slate-400 font-normal ml-1.5">({pantryItems.length} items)</span>
              </h3>
            </div>
            <div className="flex items-center gap-2">
              {runningLow.length > 0 && (
                <Badge className="bg-amber-100 text-amber-700 text-xs" data-testid="running-low-badge">
                  <AlertTriangle className="h-3 w-3 mr-1" />{runningLow.length} low
                </Badge>
              )}
              <Button
                variant="outline" size="sm"
                className="text-xs border-slate-300 text-slate-600 h-7"
                onClick={() => setExpanded(!expanded)}
                data-testid="pantry-expand-btn"
              >
                {expanded ? "Collapse" : "Manage"}
              </Button>
            </div>
          </div>

          {/* Running Low Alert */}
          {runningLow.length > 0 && !expanded && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 mb-2" data-testid="running-low-alert">
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-xs font-medium text-amber-800">
                  <AlertTriangle className="h-3 w-3 inline mr-1" />
                  These items may need restocking:
                </p>
                <button
                  onClick={() => { const n = addLowItemsToList(runningLow); }}
                  className="text-xs text-amber-700 font-medium hover:text-amber-900 flex items-center gap-1"
                  data-testid="add-low-to-list-btn"
                >
                  <ShoppingCart className="h-3 w-3" />Add All
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {runningLow.map(item => {
                  const cfg = STATUS_CONFIG[item.status];
                  return (
                    <Badge key={item.item_id} className={`text-xs ${cfg.badge}`} data-testid={`low-item-${item.item_id}`}>
                      {item.name}
                      {item.days_remaining != null && (
                        <span className="ml-1 opacity-75">({item.days_remaining}d)</span>
                      )}
                    </Badge>
                  );
                })}
              </div>
            </div>
          )}

          {/* Quick ingredient pills when collapsed */}
          {!expanded && pantryItems.length > 0 && (
            <div className="flex flex-wrap gap-1.5 max-h-12 overflow-y-auto">
              {stocked.slice(0, 10).map(item => (
                <span key={item.item_id} className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                  {item.name}
                </span>
              ))}
              {stocked.length > 10 && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                  +{stocked.length - 10} more
                </span>
              )}
            </div>
          )}

          {/* Empty State */}
          {pantryItems.length === 0 && !expanded && (
            <div className="text-center py-3">
              <p className="text-xs text-slate-500 mb-2">No pantry items yet. Set up your pantry for smarter recipes.</p>
              <Button
                size="sm" className="bg-slate-700 hover:bg-slate-800 text-white text-xs"
                onClick={openSetup}
                data-testid="setup-pantry-btn"
              >
                <Sparkles className="h-3 w-3 mr-1" />Quick Setup with AI
              </Button>
            </div>
          )}

          {/* Expanded Management View */}
          {expanded && (
            <div className="space-y-3 mt-2">
              {/* Add item input */}
              <div className="flex gap-2">
                <Input
                  value={newItemName}
                  onChange={(e) => setNewItemName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addSingleItem()}
                  placeholder="Add pantry item..."
                  className="text-sm h-8"
                  data-testid="pantry-add-input"
                />
                <Button
                  size="sm" className="bg-slate-700 hover:bg-slate-800 text-white h-8 px-3"
                  onClick={addSingleItem}
                  disabled={addingItem || !newItemName.trim()}
                  data-testid="pantry-add-btn"
                >
                  {addingItem ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                </Button>
              </div>

              {/* AI Setup button */}
              <Button
                variant="outline" size="sm"
                className="w-full text-xs border-slate-300 text-slate-600"
                onClick={openSetup}
                data-testid="ai-setup-btn"
              >
                <Sparkles className="h-3 w-3 mr-1" />AI Suggested Pantry Items
              </Button>

              {/* Running Low Section */}
              {runningLow.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-xs font-semibold text-amber-700">
                      <AlertTriangle className="h-3 w-3 inline mr-1" />Needs Restocking
                    </p>
                    <button
                      onClick={() => addLowItemsToList(runningLow)}
                      className="text-xs text-amber-600 hover:text-amber-800 font-medium flex items-center gap-1"
                      data-testid="add-all-low-btn"
                    >
                      <ShoppingCart className="h-3 w-3" />Add All to List
                    </button>
                  </div>
                  <div className="space-y-1">
                    {runningLow.map(item => {
                      const cfg = STATUS_CONFIG[item.status];
                      return (
                        <div key={item.item_id} className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-amber-200"
                          data-testid={`pantry-item-${item.item_id}`}>
                          <div className={`w-2 h-2 rounded-full ${cfg.color}`} />
                          <span className="flex-1 text-sm text-gray-800">{item.name}</span>
                          <Badge className={`text-xs ${cfg.badge}`}>{cfg.label}</Badge>
                          {item.days_remaining != null && (
                            <span className="text-xs text-gray-400 flex items-center gap-0.5">
                              <Timer className="h-3 w-3" />{item.days_remaining}d
                            </span>
                          )}
                          <span className="text-xs text-gray-400">~{item.avg_restock_days}d cycle</span>
                          <button onClick={() => addLowItemsToList([item])}
                            className="p-1 text-amber-500 hover:text-amber-700 transition-colors"
                            title="Add to grocery list"
                            data-testid={`add-to-list-${item.item_id}`}>
                            <ShoppingCart className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={() => deleteItem(item.item_id)}
                            className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                            data-testid={`delete-pantry-${item.item_id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Stocked Items */}
              {stocked.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-green-700 mb-1.5">
                    <CheckCircle2 className="h-3 w-3 inline mr-1" />Stocked
                  </p>
                  {Object.entries(groupByCategory(stocked)).map(([cat, items]) => (
                    <div key={cat} className="mb-2">
                      <p className="text-xs text-slate-400 mb-1">{cat}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {items.map(item => (
                          <span key={item.item_id}
                            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg bg-white border border-slate-200 text-slate-700"
                            data-testid={`pantry-item-${item.item_id}`}>
                            {item.name}
                            <button onClick={() => deleteItem(item.item_id)}
                              className="text-slate-300 hover:text-red-500 transition-colors ml-0.5"
                              data-testid={`delete-pantry-${item.item_id}`}>
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* AI Setup Modal */}
      {showSetup && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4"
          data-testid="pantry-setup-modal">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b flex items-center justify-between">
              <div>
                <h3 className="font-bold text-gray-900">Set Up Your Pantry</h3>
                <p className="text-xs text-gray-500 mt-0.5">Select items you usually keep at home</p>
              </div>
              <button onClick={() => setShowSetup(false)} className="p-1.5 rounded-full hover:bg-gray-100"
                data-testid="close-setup-modal">
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {loadingSuggestions ? (
                <div className="flex flex-col items-center py-8 gap-2">
                  <Loader2 className="h-6 w-6 text-slate-400 animate-spin" />
                  <p className="text-sm text-slate-500">AI is generating suggestions...</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-gray-500">{selectedSuggestions.size} of {suggestions.length} selected</p>
                    <button
                      className="text-xs text-blue-600 hover:underline"
                      onClick={() => {
                        if (selectedSuggestions.size === suggestions.length) setSelectedSuggestions(new Set());
                        else setSelectedSuggestions(new Set(suggestions.map(s => s.name)));
                      }}
                      data-testid="toggle-all-suggestions"
                    >
                      {selectedSuggestions.size === suggestions.length ? "Deselect All" : "Select All"}
                    </button>
                  </div>
                  {Object.entries(
                    suggestions.reduce((acc, s) => {
                      const cat = s.category || "Other";
                      if (!acc[cat]) acc[cat] = [];
                      acc[cat].push(s);
                      return acc;
                    }, {})
                  ).map(([cat, items]) => (
                    <div key={cat}>
                      <p className="text-xs font-semibold text-gray-600 mb-1.5">{cat}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {items.map(item => (
                          <button key={item.name}
                            onClick={() => toggleSuggestion(item.name)}
                            className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                              selectedSuggestions.has(item.name)
                                ? "bg-slate-700 text-white border-slate-700"
                                : "bg-white text-gray-600 border-gray-300 hover:border-slate-400"
                            }`}
                            data-testid={`suggest-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            {item.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-4 border-t bg-gray-50 flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowSetup(false)}>
                Cancel
              </Button>
              <Button
                className="flex-1 bg-slate-700 hover:bg-slate-800 text-white"
                onClick={saveBulk}
                disabled={savingSuggestions || selectedSuggestions.size === 0}
                data-testid="save-pantry-btn"
              >
                {savingSuggestions ? (
                  <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Saving...</>
                ) : (
                  <>Add {selectedSuggestions.size} Items</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PantrySection;
